import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { jobHasWarnings, type JobRecord } from '../../api/jobsApi';
import { useJobCenterOptional } from '../../contexts/JobCenterContext';
import { useActivePatient } from '../../contexts/ActivePatientContext';
import { useIsAdmin } from '../../auth/useIsAdmin';
import { jobPatientIds } from './jobPatients';

/**
 * The Job Center's scope, and every count derived from it, in ONE place.
 *
 * WHY THIS EXISTS. The same bug has now shipped twice, in two different
 * surfaces, for one structural reason: scoping lived inside the drawer while
 * other surfaces computed their own numbers from the unscoped context. First
 * the drawer scoped by role instead of panel; then, once that was fixed, the
 * launcher badge still read "12" (fleet-wide) above a drawer listing 2 for the
 * open patient. A count you cannot reconcile with the list under it is worse
 * than no count -- it makes the reader distrust both.
 *
 * So the rule lives here and every surface consumes it. A future surface gets
 * the right scope by construction rather than by remembering to.
 *
 * THE RULE IS THE PANEL, NOT THE ROLE. In the admin console you are running the
 * fleet; everywhere else you are mid-consult and only the open chart is
 * actionable. /admin/* is admin-gated, so "in the admin console" implies the
 * role. Server-side scoping (owner + admin) is untouched underneath: this
 * narrows what is worth showing, never what is reachable.
 */
export interface ScopedJobs {
  /** Jobs this surface should show, already scoped. */
  jobs: JobRecord[];
  /** Running/queued/retrying/recovering within the scope. */
  activeCount: number;
  /** Unacknowledged failures AND completed-with-warnings within the scope. */
  needsReviewCount: number;
  /** True in /admin/* -- the whole-fleet view. */
  inAdminConsole: boolean;
  /** Clinical workspace with no chart open: show the prompt, list nothing. */
  awaitingPatient: boolean;
  /** Role, for admin-only affordances like the link to /admin/jobs. */
  isAdmin: boolean;
  /** False when the provider is absent (the launcher renders nothing then). */
  available: boolean;
}

const ACTIVE_STATUSES = new Set(['queued', 'running', 'retrying', 'recovering']);

/** Needs a human: an outright failure, or a completed job with a broken step. */
export function jobNeedsReview(job: JobRecord): boolean {
  return job.status === 'failed' || (job.status === 'completed' && jobHasWarnings(job));
}

export function useScopedJobs(): ScopedJobs {
  const ctx = useJobCenterOptional();
  const { pathname } = useLocation();
  const { active } = useActivePatient();
  const isAdmin = useIsAdmin();

  const inAdminConsole = pathname.startsWith('/admin');
  const allJobs = ctx?.jobs;
  const isAcknowledged = ctx?.isAcknowledged;

  const jobs = useMemo(() => {
    if (!allJobs) return [];
    if (inAdminConsole) return allJobs;
    if (!active) return [];
    return allJobs.filter((j) => jobPatientIds(j).includes(active.patientId));
  }, [allJobs, inAdminConsole, active]);

  const activeCount = useMemo(
    () => jobs.filter((j) => ACTIVE_STATUSES.has(j.status)).length,
    [jobs],
  );

  // Mirrors the drawer's "Needs review" section exactly -- including the
  // acknowledgement overlay, so dismissing a card also drops the badge. The
  // context's own unreadFailedCount is fleet-wide and deliberately not reused.
  const needsReviewCount = useMemo(
    () => jobs.filter((j) => jobNeedsReview(j) && !(isAcknowledged?.(j.job_id) ?? false)).length,
    [jobs, isAcknowledged],
  );

  return {
    jobs,
    activeCount,
    needsReviewCount,
    inAdminConsole,
    awaitingPatient: !inAdminConsole && !active,
    isAdmin,
    available: Boolean(ctx),
  };
}
